import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { GENRES, BAND_STATUSES } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';
import { useProfile } from '../hooks/useProfile';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LocationSearch } from '../components/LocationSearch';
import { Users, CreditCard, AlertTriangle, Check, Hourglass, Lightbulb, Plus } from 'lucide-react';
import { optimizeImage, validateImageFile } from '../utils/imageOptimizer';
import { fetchBandDetails, updateBand } from '../services/api/band';
import type { Band } from '../types/database';

const currentYear = new Date().getFullYear();

const editBandSchema = z.object({
  name: z.string().min(3, 'Band name must be at least 3 characters').max(50, 'Band name cannot exceed 50 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description cannot exceed 500 characters'),
  location: z.string().min(2, 'Location must be at least 2 characters').max(100, 'Location cannot exceed 100 characters'),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }).nullable(),
  genres: z.array(z.string()).min(1, 'Please select at least one genre'),
  avatar_url: z.string().optional(),
  foundation_year: z.union([
    z.number().int().min(1900).max(currentYear).optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  website_url: z.union([
    z.string().url('Please enter a valid URL').optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  fan_area_enabled: z.boolean().default(false),
  fan_area_price: z.union([
    z.number().min(0, 'Price must be 0 or greater').optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  fan_area_description: z.string().max(1000, 'Description cannot exceed 1000 characters').optional(),
  status: z.string().min(1, 'Please select a status'),
});

type EditBandFormData = z.infer<typeof editBandSchema>;

function EditBand() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { getTranslatedGenres, getTranslatedBandStatuses } = useMusicTranslations();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [band, setBand] = useState<Band | null>(null);
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const requestBandVerification = async () => {
    if (!band) return;

    try {
      // Update band verification status to pending
      const { error: updateError } = await supabase
        .from('bands')
        .update({
          verification_status: 'pending',
          verification_requested_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', band.id);

      if (updateError) throw updateError;

      // Send verification request email
      const { error: emailError } = await supabase.functions.invoke('send-verification-request', {
        body: {
          bandId: band.id,
          bandName: band.name,
          bandDescription: band.description,
          bandLocation: band.location,
          bandGenres: band.genres,
          foundationYear: band.foundation_year,
          websiteUrl: band.website_url,
        },
      });

      if (emailError) {
        console.error('Error sending verification email:', emailError);
        // Don't throw error here - the database update was successful
      }

      // Reload band data to show updated status
      const updatedBandData = await fetchBandDetails(slug!);
      setBand(updatedBandData);
    } catch (err) {
      console.error('Error requesting verification:', err);
      throw err;
    }
  };

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<EditBandFormData>({
    resolver: zodResolver(editBandSchema),
  });

  const translatedGenres = getTranslatedGenres();
  const translatedBandStatuses = getTranslatedBandStatuses();

  useEffect(() => {
    async function loadBand() {
      if (!slug) return;

      try {
        const bandData = await fetchBandDetails(slug);
        setBand(bandData);
        
        // Check if we need to update Stripe status (e.g., after returning from onboarding)
        if (bandData.stripe_account_id && !bandData.stripe_onboarding_completed) {
          await checkAndUpdateStripeStatus(bandData.id, bandData.stripe_account_id);
        }
        
        reset({
          name: bandData.name,
          description: bandData.description || '',
          location: bandData.location || '',
          coordinates: bandData.coordinates,
          genres: bandData.genres || [],
          avatar_url: bandData.avatar_url || '',
          foundation_year: bandData.foundation_year || '',
          website_url: bandData.website_url || '',
          fan_area_enabled: bandData.fan_area_enabled || false,
          fan_area_price: bandData.fan_area_price || '',
          fan_area_description: bandData.fan_area_description || '',
          status: bandData.status || 'Aktiv',
        });
      } catch (err) {
        console.error('Error loading band:', err);
        setError('Failed to load band details');
      }
    }

    loadBand();
  }, [slug, reset]);

  const checkAndUpdateStripeStatus = async (bandId: string, stripeAccountId: string) => {
    try {
      setCheckingStripeStatus(true);
      
      // Call edge function to check and update Stripe account status
      const { data, error } = await supabase.functions.invoke('check-stripe-account-status', {
        body: { 
          band_id: bandId,
          stripe_account_id: stripeAccountId 
        },
      });

      if (error) {
        console.error('Error checking Stripe status:', error);
        return;
      }

      if (data?.updated) {
        // Reload the band data to get updated Stripe status
        const updatedBandData = await fetchBandDetails(slug!);
        setBand(updatedBandData);
        
        // Update the form with the latest data
        reset({
          name: updatedBandData.name,
          description: updatedBandData.description || '',
          location: updatedBandData.location || '',
          coordinates: updatedBandData.coordinates,
          genres: updatedBandData.genres || [],
          avatar_url: updatedBandData.avatar_url || '',
          foundation_year: updatedBandData.foundation_year || '',
          website_url: updatedBandData.website_url || '',
          fan_area_enabled: updatedBandData.fan_area_enabled || false,
          fan_area_price: updatedBandData.fan_area_price || '',
          fan_area_description: updatedBandData.fan_area_description || '',
        });
      }
    } catch (err) {
      console.error('Error checking Stripe account status:', err);
    } finally {
      setCheckingStripeStatus(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      setError('');

      let file = event.target.files?.[0];
      if (!file) return;

      await validateImageFile(file);

      // Optimize image before upload
      const optimized = await optimizeImage(file, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.85,
        format: 'image/webp',
      });
      file = optimized.file;

      // Generate a simple alphanumeric filename
      const fileName = `${Date.now()}${Math.random().toString(36).slice(2)}.webp`;

      // Delete old avatar if exists
      if (band?.avatar_url) {
        const oldFileName = band.avatar_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage
            .from('bands')
            .remove([oldFileName]);
        }
      }

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from('bands')
        .upload(fileName, file, {
          cacheControl: '31536000',
          contentType: 'image/webp'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('bands')
        .getPublicUrl(fileName);

      // Update form with avatar URL
      setValue('avatar_url', publicUrl);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleGenreChange = (genre: string) => {
    const currentGenres = watch('genres', []);
    const newGenres = currentGenres.includes(genre)
      ? currentGenres.filter(g => g !== genre)
      : [...currentGenres, genre];
    setValue('genres', newGenres);
  };

  const handleLocationChange = (location: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('location', location.name);
    setValue('coordinates', location.coordinates);
  };

  const onSubmit = async (data: EditBandFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    if (!band) return;

    // Validate Fan Area settings if enabled
    if (data.fan_area_enabled) {
      if (band.verification_status !== 'approved') {
        setError(t('fanArea.validation.verificationRequired'));
        return;
      }
      if (!(band.stripe_onboarding_completed && band.stripe_charges_enabled && band.stripe_payouts_enabled)) {
        setError(t('fanArea.validation.stripeSetupRequired'));
        return;
      }
      if (!data.fan_area_price || data.fan_area_price <= 0) {
        setError(t('fanArea.validation.priceRequired'));
        return;
      }
      if (!data.fan_area_description || data.fan_area_description.trim().length < 10) {
        setError(t('fanArea.validation.descriptionRequired'));
        return;
      }
    }

    try {
      setIsSubmitting(true);
      await updateBand(band.id, data);
      navigate(`/bands/${band.slug}`);
    } catch (err) {
      console.error('Error updating band:', err);
      setError(err instanceof Error ? err.message : 'Failed to update band');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!band) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: band.name, href: `/bands/${slug}` },
    { label: t('bands.editBand') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('forms.updateBand')}>
        {t('bands.editBand')}
      </Title>

      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="bg-primary/5 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="mb-6 flex flex-col gap-4 sm:gap-2 sm:items-center sm:flex-row sm:space-x-4">
              <div className="w-32 h-32 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {watch('avatar_url') ? (
                  <img
                    src={watch('avatar_url')}
                    alt="Band avatar"
                    className="w-full h-full object-cover"
                   loading="lazy" />
                ) : (
                  <Users className="w-12 h-12 text-light/60" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-outline"
                >
                  {uploading ? t('forms.uploading') : t('forms.changeBandPhoto')}
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-light/80">
                  {t('forms.bandName')}
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('forms.location')}
                </label>
                <LocationSearch
                  value={watch('location') || ''}
                  onChange={handleLocationChange}
                  error={errors.location?.message}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="foundation_year" className="block text-sm font-medium text-light/80">
                    {t('common.foundationYear')} ({t('common.optional')})
                  </label>
                  <input
                    type="number"
                    id="foundation_year"
                    min="1900"
                    max={currentYear}
                    {...register('foundation_year', { valueAsNumber: true })}
                    className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                    placeholder="2025"
                  />
                  {errors.foundation_year && (
                    <p className="mt-1 text-sm text-red-500">{errors.foundation_year.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="website_url" className="block text-sm font-medium text-light/80">
                    {t('common.websiteUrl')} ({t('common.optional')})
                  </label>
                  <input
                    type="url"
                    id="website_url"
                    {...register('website_url')}
                    className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                    placeholder="https://example.com"
                  />
                  {errors.website_url && (
                    <p className="mt-1 text-sm text-red-500">{errors.website_url.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-light/80">
                  {t('forms.bandDescription')}
                </label>
                <textarea
                  id="description"
                  rows={4}
                  {...register('description')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                  placeholder={t('forms.tellAboutBand')}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">Status</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {translatedBandStatuses.map((status) => (
                <label key={status.value} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value={status.value}
                    {...register('status')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{status.label}</span>
                </label>
              ))}
            </div>
            {errors.status && (
              <p className="mt-4 text-sm text-red-500">{errors.status.message}</p>
            )}
          </div>

          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">{t('forms.bandGenres')}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {translatedGenres.map((genre) => (
                <label key={genre.value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={watch('genres', []).includes(genre.value)}
                    onChange={() => handleGenreChange(genre.value)}
                    className="h-4 w-4 rounded border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{genre.label}</span>
                </label>
              ))}
            </div>
            {errors.genres && (
              <p className="mt-4 text-sm text-red-500">{errors.genres.message}</p>
            )}
          </div>

          {/* Fan Area Section */}
          <div  style={{
                    display: 'none',
                  }} className="panel">
            <h3 className="text-xl font-semibold mb-6">Fan Zone</h3>
            
            <div className="space-y-6">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={watch('fan_area_enabled')}
                    onChange={(e) => setValue('fan_area_enabled', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">
                    Fan Zone aktivieren
                  </span>
                </label>
              </div>

              {watch('fan_area_enabled') && (
                <>
                  {/* Band Verification Section */}
                  <div className="mb-6">
                    {band.verification_status === 'approved' ? (
                      <div className="bg-green-500/10 text-green-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-2" />
                          {t('fanArea.verification.verified')}
                        </h4>
                        <p className="text-sm">
                          {t('fanArea.verification.verifiedDescription')}
                        </p>
                        {band.verification_approved_at && (
                          <p className="text-sm mt-1">
                            {t('fanArea.verification.verifiedOn')}: {new Date(band.verification_approved_at).toLocaleDateString('de-CH')}
                          </p>
                        )}
                      </div>
                    ) : band.verification_status === 'pending' ? (
                      <div className="bg-yellow-500/10 text-yellow-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center">
                          <Hourglass className="w-5 h-5 text-yellow-500 mr-2" />
                          {t('fanArea.verification.pending')}
                        </h4>
                        <p className="text-sm">
                          {t('fanArea.verification.pendingDescription')}
                        </p>
                        {band.verification_requested_at && (
                          <p className="text-sm mt-1">
                            {t('fanArea.verification.requestedOn')}: {new Date(band.verification_requested_at).toLocaleDateString('de-CH')}
                          </p>
                        )}
                      </div>
                    ) : band.verification_status === 'rejected' ? (
                      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center">
                          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                          {t('fanArea.verification.rejected')}
                        </h4>
                        <p className="text-sm">
                          {t('fanArea.verification.rejectedDescription')}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await requestBandVerification();
                            } catch (err) {
                              console.error('Error requesting verification:', err);
                              setError(t('fanArea.verification.requestError'));
                            }
                          }}
                          className="btn-primary text-sm mt-3"
                        >
                          {t('fanArea.verification.requestAgain')}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center">
                          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                          {t('fanArea.verification.required')}
                        </h4>
                        <p className="text-sm mb-3">
                          {t('fanArea.verification.requiredDescription')}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await requestBandVerification();
                            } catch (err) {
                              console.error('Error requesting verification:', err);
                              setError(t('fanArea.verification.requestError'));
                            }
                          }}
                          className="btn-primary text-sm"
                        >
                          {t('fanArea.verification.requestVerification')}
                        </button>
                      </div>
                    )}
                  </div>

                   {/* Stripe Connect Status */}
                  <div className="mb-6">
                    {checkingStripeStatus && (
                      <div className="bg-blue-500/10 text-blue-400 p-4 rounded-lg mb-4">
                        <p className="text-sm">Checking Stripe account status...</p>
                      </div>
                    )}
                    
                    {band.stripe_onboarding_completed && band.stripe_charges_enabled && band.stripe_payouts_enabled ? (
                      <div className="bg-green-500/10 text-green-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center"><Check className="w-5 h-5 text-green-500 mr-2" /> {t('fanArea.stripe.connected')}</h4>
                        <p className="text-sm mb-2">{t('fanArea.stripe.connectedDescription')}</p>
                      </div>
                    ) : band.stripe_onboarding_completed ? (
                      <div className="bg-yellow-500/10 text-yellow-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center"><AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" /> {t('fanArea.stripe.partiallySetup')}</h4>
                        <p className="text-sm mb-2">{t('fanArea.stripe.partiallySetupDescription')}</p>
                        <ul className="text-sm space-y-1 list-disc list-inside">
                          <li>{t('fanArea.stripe.chargesEnabled')}: {band.stripe_charges_enabled ? t('common.yes') : t('common.no')}</li>
                          <li>{t('fanArea.stripe.payoutsEnabled')}: {band.stripe_payouts_enabled ? t('common.yes') : t('common.no')}</li>
                        </ul>
                        <p className="text-sm mt-2">
                          {t('fanArea.stripe.completeVerification')}
                        </p>
                      </div>
                    ) : band.stripe_account_id ? (
                      <div className="bg-yellow-500/10 text-yellow-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center"><Hourglass className="w-5 h-5 text-yellow-500 mr-2" /> {t('fanArea.stripe.onboardingInProgress')}</h4>
                        <p className="text-sm mb-3">
                          {t('fanArea.stripe.onboardingInProgressDescription')}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data, error } = await supabase.functions.invoke('create-stripe-connect-account', {
                                body: { band_id: band.id }
                              });
                              if (error) throw error;
                              if (data.onboarding_url) {
                                window.open(data.onboarding_url, '_blank');
                              }
                            } catch (err) {
                              console.error('Error creating Stripe account:', err);
                              let errorMessage = 'Failed to create Stripe account';
                              
                              if (err?.message?.includes('You can only create new accounts if you\'ve signed up for Connect')) {
                                errorMessage = 'Stripe Connect is not enabled for your account. Please visit your Stripe Dashboard and enable Connect, or contact Stripe support to activate Connect for your account.';
                              } else {
                                errorMessage = err?.context?.body?.error || 
                                             err?.message || 
                                             'Failed to create Stripe account';
                              }
                              
                              setError(errorMessage);
                            }
                          }}
                          className="btn-primary text-sm"
                        >
                          {t('fanArea.stripe.completeSetup')}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center"><CreditCard className="w-5 h-5 text-red-500 mr-2" /> {t('fanArea.stripe.connectRequired')}</h4>
                        <p className="text-sm mb-3">
                          {t('fanArea.stripe.connectRequiredDescription')}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data, error } = await supabase.functions.invoke('create-stripe-connect-account', {
                                body: { band_id: band.id }
                              });
                              if (error) throw error;
                              if (data.onboarding_url) {
                                window.open(data.onboarding_url, '_blank');
                              }
                            } catch (err) {
                              console.error('Error creating Stripe account:', err);
                              let errorMessage = 'Failed to create Stripe account';
                              
                              if (err?.message?.includes('You can only create new accounts if you\'ve signed up for Connect')) {
                                errorMessage = 'Stripe Connect is not enabled for your account. Please visit your Stripe Dashboard and enable Connect, or contact Stripe support to activate Connect for your account.';
                              } else {
                                errorMessage = err?.context?.body?.error || 
                                             err?.message || 
                                             'Failed to create Stripe account';
                              }
                              
                              setError(errorMessage);
                            }
                          }}
                          className="btn-primary text-sm"
                        >
                          {t('fanArea.stripe.connectAccount')}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-blue-500/10 text-blue-400 p-4 rounded-lg mb-6">
                    <h4 className="font-semibold mb-2 flex items-center"><Lightbulb className="w-5 h-5 text-blue-400 mr-2" /> {t('fanArea.stripe.howItWorks')}</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>{t('fanArea.stripe.howItWorksPoint1')}</li>
                      <li>{t('fanArea.stripe.howItWorksPoint2')}</li>
                      <li>{t('fanArea.stripe.howItWorksPoint3')}</li>
                      <li>{t('fanArea.stripe.howItWorksPoint3')}</li>
                      <li>{t('fanArea.stripe.howItWorksPoint4')}</li>
                      <li>{t('fanArea.stripe.howItWorksPoint5')}</li>
                    </ul>
                  </div>
                  
                  {!(band.stripe_onboarding_completed && band.stripe_charges_enabled && band.stripe_payouts_enabled) && (
                    <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center"><AlertTriangle className="w-5 h-5 text-red-500 mr-2" /> {t('fanArea.settings.stripeRequired')}</h4>
                      <p className="text-sm">
                        {t('fanArea.settings.stripeRequiredDescription')}
                      </p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="fan_area_price" className="block text-sm font-medium text-light/80">
                      Einmaliger Fan-Preis (CHF)
                    </label>
                    <input
                      type="number"
                      id="fan_area_price"
                      step="0.01"
                      min="0"
                      {...register('fan_area_price', { valueAsNumber: true })}
                      className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                      placeholder="5.00"
                    />
                    <p className="mt-1 text-sm text-light/60">
                      {t('fanArea.settings.priceDescription')}
                    </p>
                    {errors.fan_area_price && (
                      <p className="mt-1 text-sm text-red-500">{errors.fan_area_price.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="fan_area_description" className="block text-sm font-medium text-light/80">
                      Fan Area Beschreibung
                    </label>
                    <textarea
                      id="fan_area_description"
                      rows={6}
                      {...register('fan_area_description')}
                      className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                      placeholder="Beschreibe, was Fans mit ihrer Unterstützung erhalten:"
                    />
                    <p className="mt-1 text-sm text-light/60">
                      Erkläre, welche exklusiven Inhalte und Vorteile Fans erhalten. Sei spezifisch darüber, was die Fan-Unterstützung wertvoll macht.
                    </p>
                    {errors.fan_area_description && (
                      <p className="mt-1 text-sm text-red-500">{errors.fan_area_description.message}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/bands/${slug}`)}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBand;